import {
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconFilter, IconFilterOff } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getDebtorOrderHistories } from "api/debtor.service";
import { MAX_INTEGER } from "common/constants";
import { navigateTo } from "common/navigation";
import { formatDate } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import OrderHistoryFilter from "pages/admin/components/OrderHistoryFilter";
import { DefaultTableLayout } from "pages/admin/dataEntry/TableGridDefinitions";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginatedDebtorOrderHistories } from "types/model.types";

const AdminDebtorOrderHistory: React.FC = () => {
  const [params] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [productFrom, setProductFrom] = useState<string>("");
  const [productTo, setProductTo] = useState<string>(""); // max int value to ensure it includes all products if not specified
  const id = params.get("id");
  const [recordId, setRecordId] = useState<number | undefined>(undefined);
  const [activePage, setActivePage] = useState(1);
  const [loading, setloading] = useState<boolean>(false);
  const [transactions, setTransactions] =
    useState<PaginatedDebtorOrderHistories>({
      data: [],
      total: 1,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });

  useEffect(() => {
    if (!id) return;

    const numeric = Number(id);
    const isNumeric = !Number.isNaN(numeric);

    if (!isNumeric) {
      setTransactions({
        data: [],
        total: 1,
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      return;
    }

    setRecordId(numeric);
    if (numeric !== recordId) setActivePage(1);
  }, [id]);

  const fetchData = useCallback(async () => {
    if (!recordId) return;

    setloading(true);

    try {
      const normalizedDateFrom = dateFrom && dateFrom !== "" ? dateFrom : null;
      const normalizedDateTo = dateTo && dateTo !== "" ? dateTo : null;

      const normalizedProductFrom =
        productFrom && productFrom !== "" ? Number(productFrom) : 0;

      const normalizedProductTo =
        productTo && productTo !== "" ? Number(productTo) : MAX_INTEGER;

      const response = await safeApiCall(
        getDebtorOrderHistories(
          Number(id),
          activePage,
          DEFAULT_PAGE_SIZE,
          false,
          normalizedDateFrom,
          normalizedDateTo,
          normalizedProductFrom,
          normalizedProductTo,
        ),
      );

      if (response) {
        setTransactions(response);
      }
    } finally {
      setloading(false);
    }
  }, [recordId, activePage, id, dateFrom, dateTo, productFrom, productTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = transactions.data.map((item) => {
    return (
      <Table.Tr key={item.id}>
        <Table.Td>{formatDate(item.createdOn)}</Table.Td>
        <Table.Td
          className="link"
          onClick={() => {
            navigateTo(`/print/orders?id=${item.reference}&s=2&e=0`);
          }}
        >
          {item.invoiceId}
        </Table.Td>
        <Table.Td
          className="link"
          onClick={() => {
            navigateTo(`/print/orders?id=${item.reference}&s=1&e=0`);
          }}
        >
          {item.reference}
        </Table.Td>
        <Table.Td
          className="link"
          onClick={() => {
            navigateTo(`/admin/enquire-products?id=${item.productId}`);
          }}
        >{`${item.productId}${(item.productCode ?? "").toUpperCase()}`}</Table.Td>
        <Table.Td className="notes">{item.productDescription}</Table.Td>
        <Table.Td className="number">{item.quantity}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack mt={10}>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" align="center" mb="md" wrap="nowrap">
            {/* LEFT SIDE FILTERS */}
            <Group
              justify="space-between"
              align="flex-end"
              mb="md"
              wrap="nowrap"
              style={{ minWidth: 0 }} // ← FIXES THE POPUP WIDTH COLLAPSE
            >
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFilter size={14} />}
                onClick={() => setFiltersOpen(true)}
              >
                Filters
              </Button>

              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconFilterOff size={14} />}
                onClick={() => {
                  setDateFrom(null);
                  setDateTo(null);
                  setProductFrom("");
                  setProductTo("");
                  setActivePage(1);
                  fetchData();
                }}
              >
                Reset Filters
              </Button>
            </Group>
            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                disabled={loading}
                total={Math.ceil(transactions.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>

          <DefaultTableLayout>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th className="money">Invoice #</Table.Th>
                <Table.Th className="money">P/S #</Table.Th>
                <Table.Th>Product Code</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th className="money">Quantity</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </DefaultTableLayout>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              disabled={loading}
              total={Math.ceil(transactions.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      <OrderHistoryFilter
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        dateFrom={dateFrom}
        dateTo={dateTo}
        productFrom={productFrom}
        productTo={productTo}
        onApply={(filters: any) => {
          setDateFrom(filters.dateFrom);
          setDateTo(filters.dateTo);
          setProductFrom(filters.productFrom);
          setProductTo(filters.productTo);
          setActivePage(1);

          fetchData(); // <-- IMMEDIATE FETCH TO UPDATE THE TABLE WITH NEW FILTERS

          // Note: fetchData will be automatically called due to the dependency array in useEffect
        }}
      />
    </Stack>
  );
};

export default AdminDebtorOrderHistory;
